import { acknowledgePlayBilling } from "api/subscriptions";
import { captureException, captureMessage } from "@sentry/nextjs";

import { useState } from "react";
import { useMounted } from "hooks/react";
import { useNotifications } from "hooks/notifications";
import { useRouter } from "next/router";
import { useQueryClient } from "react-query";

type PlayBillingSubscriptionProps = {
  user: any;
};

const SKU = process.env.NEXT_PUBLIC_GOOGLE_PLAY_BILLING_SUBSCRIPTION_SKU;
const PRICE =
  process.env.NEXT_PUBLIC_GOOGLE_PLAY_BILLING_SUBSCRIPTION_PRICE || "19.90";
const FORMATTED_PRICE = `R$ ${PRICE.replace(".", ",")}`;

export default function PlayBillingSubscription({
  user
}: PlayBillingSubscriptionProps): JSX.Element {
  const [playBillingService, setPlayBillingService] = useState(null);
  const [skuDetails, setSkuDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const { setNotification } = useNotifications();
  const router = useRouter();

  const queryClient = useQueryClient();

  useMounted(() => {
    const initPlayBilling = async () => {
      // @ts-ignore - Digital Goods API is not yet in TypeScript defs
      const service = await window.getDigitalGoodsService(
        "https://play.google.com/billing"
      );
      setPlayBillingService(service);

      const details = await service.getDetails([SKU]);
      if (details && details.length > 0) {
        setSkuDetails(details[0]);
      }
    };

    initPlayBilling();
  });

  const handlePurchase = async () => {
    if (!playBillingService || !skuDetails) return;

    setLoading(true);

    const paymentMethods = [
      {
        supportedMethods: "https://play.google.com/billing",
        data: { sku: SKU }
      }
    ];

    const paymentDetails = {
      total: {
        label: `Vstiba Total`,
        amount: { currency: `BRL`, value: PRICE }
      }
    };

    const request = new PaymentRequest(paymentMethods, paymentDetails);

    try {
      const paymentResponse = await request.show(paymentDetails);
      const { purchaseToken } = paymentResponse.details;

      const response = await acknowledgePlayBilling({
        purchaseToken,
        sku: SKU,
        userId: user.id
      });

      if (response.body && !response.body.error) {
        await paymentResponse.complete("success");
        queryClient.invalidateQueries();
        return router.push("/assinatura/sucesso");
      }

      captureMessage("Failed to acknowledge purchase", {
        extra: { error: response.body.error, purchaseToken }
      });
      await paymentResponse.complete("fail");
      notifyFailure();
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;

      captureException(e);
      notifyFailure();
    } finally {
      setLoading(false);
    }
  };

  const notifyFailure = () => {
    setNotification({
      title: "Desculpe, algo deu errado",
      text: "A assinatura não pôde ser configurada automaticamente, mas nosso time já está investigando e vai entrar em contato com você se for preciso.",
      type: "error"
    });
  };

  const formatPrice = (details) => {
    if (!details || !details.price) return FORMATTED_PRICE;

    return new Intl.NumberFormat(navigator.language, {
      style: "currency",
      currency: details.price.currency
    }).format(details.price.value);
  };

  return (
    <div className="text-center">
      <div className="mb-4">
        <span className="text-5xl font-bold">
          {skuDetails ? formatPrice(skuDetails) : FORMATTED_PRICE}
        </span>{" "}
        <span className="text-emphasis-medium text-lg">/ mês</span>
      </div>
      <button
        onClick={handlePurchase}
        disabled={loading}
        className="btn btn-primary fade-loading w-full md:w-64"
      >
        {loading ? "Processando..." : "Assinar agora"}
      </button>
    </div>
  );
}
